package app.api

import app.client
import app.model.*
import app.plugin.Routing
import dev.fritz2.core.RootStore
import io.ktor.client.call.*
import io.ktor.client.plugins.resources.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.coroutines.Job
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json


class ProjectStepApi : RootStore<List<Step>>(emptyList(), job = Job()) {
    enum class StepType {
        SLACK_NOTIFICATION,
        UPDATE_LAMBDA,
        UPLOAD_S3,
        UPDATE_DT,
        UPDATE_DT_SBOM,
        ECS_DEPLOYMENT,
        INVALIDATE_CLOUDFRONT,
    }

    sealed class StepUpdate {
        data class Name(val value: String) : StepUpdate()
        data class ExecutionCondition(val value: app.model.ExecutionCondition) : StepUpdate()
        data class RunCondition(val value: app.model.RunCondition) : StepUpdate()
        data class AwsRegion(val value: String) : StepUpdate()
        data class AwsAccount(val value: String) : StepUpdate()
        data class ClusterName(val value: String) : StepUpdate()
        data class ServiceName(val value: String) : StepUpdate()
        data class TaskDefName(val value: String) : StepUpdate()
        data class WebhookUrl(val value: String) : StepUpdate()
        data class FunctionName(val value: String) : StepUpdate()
        data class DeployableName(val value: String) : StepUpdate()
        data class CnameTarget(val value: String) : StepUpdate()
        data class InvalidationPath(val value: String) : StepUpdate()
        data class WaitForInvalidation(val value: Boolean) : StepUpdate()
        data class BucketURI(val value: String) : StepUpdate()
        data class ProjectName(val value: String) : StepUpdate()
        data class ParentProjectUUID(val value: String) : StepUpdate()
        data class ApiUrl(val value: String) : StepUpdate()
        data class ApiKey(val value: String) : StepUpdate()
        data class ContainerDefinition(val index: Int, val definition: app.model.ContainerDefinition) : StepUpdate()
        data object AddContainer : StepUpdate()
        data object RemoveContainer : StepUpdate()
    }

    val dirtyStore = RootStore(false, job = Job())

    val handler = handle { _, id: String ->
        dirtyStore.update(false)
        client.get(Routing.ProjectSteps.Id(id = id.toInt())).body<List<Step>>()
    }

    val putProjectStep = handle {steps, projectId: String ->
        dirtyStore.update(false)
        val request = Routing.ProjectSteps.Update.Body(
            projectId = projectId.toInt(),
            steps = steps
        )
        client.put(Routing.ProjectSteps.Update()) {
            contentType(ContentType.Application.Json)
            setBody(Json.encodeToString(request))
        }.body<List<Step>>()
    }

    val updateStep = handle { steps, payload: Pair<Int, StepUpdate> ->
        dirtyStore.update(true)
        val (index, update) = payload
        steps.mapIndexed { i, step ->
            if (i != index) return@mapIndexed step

            when (step) {
                is EcsDeploymentStep -> when (update) {
                    is StepUpdate.Name -> step.copy(name = update.value)
                    is StepUpdate.ExecutionCondition -> step.copy(executionCondition = update.value)
                    is StepUpdate.RunCondition -> step.copy(runCondition = update.value)
                    is StepUpdate.AwsRegion -> step.copy(awsRegion = update.value)
                    is StepUpdate.AwsAccount -> step.copy(awsAccount = update.value)
                    is StepUpdate.ClusterName -> step.copy(clusterName = update.value)
                    is StepUpdate.ServiceName -> step.copy(serviceName = update.value)
                    is StepUpdate.TaskDefName -> step.copy(taskDefName = update.value)
                    is StepUpdate.ContainerDefinition -> step.copy(
                        containerDefinitions = step.containerDefinitions.mapIndexed { j, container ->
                            if (j == update.index) update.definition else container
                        }
                    )
                    is StepUpdate.AddContainer -> step.copy(
                        containerDefinitions = step.containerDefinitions + ContainerDefinition("", "")
                    )
                    is StepUpdate.RemoveContainer -> if (step.containerDefinitions.isNotEmpty()) {
                        step.copy(containerDefinitions = step.containerDefinitions.dropLast(1))
                    } else step
                    else -> step
                }
                is SlackNotificationStep -> when (update) {
                    is StepUpdate.Name -> step.copy(name = update.value)
                    is StepUpdate.ExecutionCondition -> step.copy(executionCondition = update.value)
                    is StepUpdate.RunCondition -> step.copy(runCondition = update.value)
                    is StepUpdate.WebhookUrl -> step.copy(webhookUrl = update.value)
                    else -> step
                }
                is UpdateLambdaStep -> when (update) {
                    is StepUpdate.Name -> step.copy(name = update.value)
                    is StepUpdate.ExecutionCondition -> step.copy(executionCondition = update.value)
                    is StepUpdate.RunCondition -> step.copy(runCondition = update.value)
                    is StepUpdate.AwsRegion -> step.copy(awsRegion = update.value)
                    is StepUpdate.AwsAccount -> step.copy(awsAccount = update.value)
                    is StepUpdate.FunctionName -> step.copy(functionName = update.value)
                    is StepUpdate.DeployableName -> step.copy(deployableName = update.value)
                    else -> step
                }
                is InvalidateCloudfrontStep -> when (update) {
                    is StepUpdate.Name -> step.copy(name = update.value)
                    is StepUpdate.ExecutionCondition -> step.copy(executionCondition = update.value)
                    is StepUpdate.RunCondition -> step.copy(runCondition = update.value)
                    is StepUpdate.AwsAccount -> step.copy(awsAccount = update.value)
                    is StepUpdate.CnameTarget -> step.copy(cnameTarget = update.value)
                    is StepUpdate.InvalidationPath -> step.copy(invalidationPath = update.value)
                    is StepUpdate.WaitForInvalidation -> step.copy(waitForInvalidation = update.value)
                    else -> step
                }
                is UploadS3Step -> when (update) {
                    is StepUpdate.Name -> step.copy(name = update.value)
                    is StepUpdate.ExecutionCondition -> step.copy(executionCondition = update.value)
                    is StepUpdate.RunCondition -> step.copy(runCondition = update.value)
                    is StepUpdate.AwsAccount -> step.copy(awsAccount = update.value)
                    is StepUpdate.AwsRegion -> step.copy(awsRegion = update.value)
                    is StepUpdate.DeployableName -> step.copy(deployableName = update.value)
                    is StepUpdate.BucketURI -> step.copy(bucketURI = update.value)
                    else -> step
                }
                is UpdateDtStep -> when (update) {
                    is StepUpdate.Name -> step.copy(name = update.value)
                    is StepUpdate.ExecutionCondition -> step.copy(executionCondition = update.value)
                    is StepUpdate.RunCondition -> step.copy(runCondition = update.value)
                    is StepUpdate.ProjectName -> step.copy(projectName = update.value)
                    is StepUpdate.ParentProjectUUID -> step.copy(parentProjectUUID = update.value)
                    is StepUpdate.ApiUrl -> step.copy(apiUrl = update.value)
                    is StepUpdate.ApiKey -> step.copy(apiKey = update.value)
                    else -> step
                }
                is UpdateDtSbomStep -> when (update) {
                    is StepUpdate.Name -> step.copy(name = update.value)
                    is StepUpdate.ExecutionCondition -> step.copy(executionCondition = update.value)
                    is StepUpdate.RunCondition -> step.copy(runCondition = update.value)
                    is StepUpdate.ProjectName -> step.copy(projectName = update.value)
                    is StepUpdate.ApiUrl -> step.copy(apiUrl = update.value)
                    is StepUpdate.ApiKey -> step.copy(apiKey = update.value)
                    else -> step
                }
                is IsEvenStep -> step //Internal Step for Integration-Tests, not visible in Frontend -> when expression must be exhaustive
            }
        }
    }

    val updateStepOrdering = handle { steps, payload: Pair<Int, Boolean> ->
        dirtyStore.update(true)
        val (currentOrdering, up) = payload
        if (up && currentOrdering <= 1) return@handle steps
        if (!up && currentOrdering >= steps.size) return@handle steps

        val targetOrdering = if (up) currentOrdering - 1 else currentOrdering + 1

        steps.map { step ->
            when (step.ordering) {
                currentOrdering -> when (step) {
                    is EcsDeploymentStep -> step.copy(ordering = targetOrdering)
                    is SlackNotificationStep -> step.copy(ordering = targetOrdering)
                    is UpdateLambdaStep -> step.copy(ordering = targetOrdering)
                    is InvalidateCloudfrontStep -> step.copy(ordering = targetOrdering)
                    is UploadS3Step -> step.copy(ordering = targetOrdering)
                    is UpdateDtStep -> step.copy(ordering = targetOrdering)
                    is UpdateDtSbomStep -> step.copy(ordering = targetOrdering)
                    is IsEvenStep -> step.copy(ordering = targetOrdering)
                }
                targetOrdering -> when (step) {
                    is EcsDeploymentStep -> step.copy(ordering = currentOrdering)
                    is SlackNotificationStep -> step.copy(ordering = currentOrdering)
                    is UpdateLambdaStep -> step.copy(ordering = currentOrdering)
                    is InvalidateCloudfrontStep -> step.copy(ordering = currentOrdering)
                    is UploadS3Step -> step.copy(ordering = currentOrdering)
                    is UpdateDtStep -> step.copy(ordering = currentOrdering)
                    is UpdateDtSbomStep -> step.copy(ordering = currentOrdering)
                    is IsEvenStep -> step.copy(ordering = currentOrdering)
                }
                else -> step
            }
        }.sortedBy { it.ordering }
    }

    val addStep = handle { steps, stepType: StepType ->
        dirtyStore.update(true)
        val newOrdering = steps.size + 1
        steps + when (stepType) {
            StepType.SLACK_NOTIFICATION -> SlackNotificationStep(
                name = "", ordering = newOrdering,
                executionCondition = ExecutionCondition.Always,
                runCondition = RunCondition.Success,
                webhookUrl = ""
            )
            StepType.UPDATE_LAMBDA -> UpdateLambdaStep(
                name = "", ordering = newOrdering,
                executionCondition = ExecutionCondition.Always,
                runCondition = RunCondition.Success,
                awsRegion = "", awsAccount = "", functionName = "", deployableName = "",
            )
            StepType.INVALIDATE_CLOUDFRONT -> InvalidateCloudfrontStep(
                name = "", ordering = newOrdering,
                executionCondition = ExecutionCondition.Always,
                runCondition = RunCondition.Success,
                awsAccount = "", cnameTarget = "", invalidationPath = "", waitForInvalidation = true,
            )
            StepType.UPLOAD_S3 -> UploadS3Step(
                name = "", ordering = newOrdering,
                executionCondition = ExecutionCondition.Always,
                runCondition = RunCondition.Success,
                awsRegion = "", awsAccount = "", deployableName = "", bucketURI = "",
            )
            StepType.UPDATE_DT -> UpdateDtStep(
                name = "", ordering = newOrdering,
                executionCondition = ExecutionCondition.Always,
                runCondition = RunCondition.Success,
                projectName = "", parentProjectUUID = "", apiUrl = "", apiKey = "",
            )
            StepType.UPDATE_DT_SBOM -> UpdateDtSbomStep(
                name = "", ordering = newOrdering,
                executionCondition = ExecutionCondition.Always,
                runCondition = RunCondition.Success,
                projectName = "", apiUrl = "", apiKey = "",
            )
            StepType.ECS_DEPLOYMENT -> EcsDeploymentStep(
                name = "", ordering = newOrdering,
                executionCondition = ExecutionCondition.Always,
                runCondition = RunCondition.Success,
                awsRegion = "", awsAccount = "",
                clusterName = "", serviceName = "", taskDefName = "",
                containerDefinitions = emptyList()
            )
        }
    }

    val deleteStep = handle { steps, ordering: Int ->
        dirtyStore.update(true)
        steps.filter { it.ordering != ordering }
            .mapIndexed { index, step ->
                when (step) {
                    is EcsDeploymentStep -> step.copy(ordering = index + 1)
                    is SlackNotificationStep -> step.copy(ordering = index + 1)
                    is UpdateLambdaStep -> step.copy(ordering = index + 1)
                    is InvalidateCloudfrontStep -> step.copy(ordering = index + 1)
                    is UploadS3Step -> step.copy(ordering = index + 1)
                    is UpdateDtStep -> step.copy(ordering = index + 1)
                    is UpdateDtSbomStep -> step.copy(ordering = index + 1)
                    is IsEvenStep -> step.copy(ordering = index + 1)
                }
            }
    }
}
